/** Single-line text for previews and counters — collapses all whitespace. */
export function htmlToPlainText(html: string): string {
  if (typeof document === 'undefined') return html.trim()
  const div = document.createElement('div')
  div.innerHTML = html
  return div.textContent.replace(/\s+/g, ' ').trim()
}

const BLOCK_TAG = /^(?:P|DIV|LI|H[1-6]|BLOCKQUOTE|PRE|TR)$/

/** Editor formatting carried into an SMS as plain characters. */
interface TextStyle {
  bold: boolean
  italic: boolean
  underline: boolean
  strike: boolean
}

const PLAIN_STYLE: TextStyle = {
  bold: false,
  italic: false,
  underline: false,
  strike: false,
}

/** Inline mark tags → the style flag they switch on for their subtree. */
const MARK_TAGS: Partial<Record<string, keyof TextStyle>> = {
  STRONG: 'bold',
  B: 'bold',
  EM: 'italic',
  I: 'italic',
  U: 'underline',
  S: 'strike',
  DEL: 'strike',
  STRIKE: 'strike',
}

/** Re-base a letter/digit into one of the Unicode math-sans alphabets (𝗮/𝘢/𝙖);
 *  anything without a styled counterpart passes through unchanged. */
function mapAlphabet(
  ch: string,
  upper: number,
  lower: number,
  digit?: number,
): string {
  const code = ch.codePointAt(0) ?? 0
  if (code >= 65 && code <= 90) return String.fromCodePoint(upper + code - 65)
  if (code >= 97 && code <= 122) return String.fromCodePoint(lower + code - 97)
  if (digit !== undefined && code >= 48 && code <= 57)
    return String.fromCodePoint(digit + code - 48)
  return ch
}

function styleChar(ch: string, style: TextStyle): string {
  if (/\s/.test(ch)) return ch
  let out = ch
  if (style.bold && style.italic)
    out = mapAlphabet(out, 0x1d63c, 0x1d656, 0x1d7ec)
  else if (style.bold) out = mapAlphabet(out, 0x1d5d4, 0x1d5ee, 0x1d7ec)
  else if (style.italic) out = mapAlphabet(out, 0x1d608, 0x1d622)
  if (style.underline) out += '\u0332'
  if (style.strike) out += '\u0336'
  return out
}

function styleText(text: string, style: TextStyle): string {
  if (!style.bold && !style.italic && !style.underline && !style.strike)
    return text
  return Array.from(text)
    .map((ch) => styleChar(ch, style))
    .join('')
}

interface ListLevel {
  ordered: boolean
  index: number
}

/** A list item renders as its `• ` / `N. ` marker plus the item's content on
 *  one block — inner block newlines are kept, trailing ones collapse so items
 *  stack without blank lines between them. */
function collectListItem(
  el: HTMLElement,
  out: string[],
  style: TextStyle,
  lists: ListLevel[],
): void {
  const level = lists[lists.length - 1] as ListLevel | undefined
  const marker = level?.ordered === true ? `${++level.index}. ` : '• '
  const sub: string[] = []
  el.childNodes.forEach((child) => collectStyledText(child, sub, style, lists))
  out.push(marker + sub.join('').replace(/\n+$/, ''), '\n')
}

function collectStyledText(
  node: Node,
  out: string[],
  style: TextStyle,
  lists: ListLevel[],
): void {
  if (node.nodeType === Node.TEXT_NODE) {
    out.push(styleText((node as Text).data, style))
    return
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return
  const el = node as HTMLElement
  const tag = el.tagName
  if (tag === 'BR') {
    out.push('\n')
    return
  }
  const mark = MARK_TAGS[tag]
  const childStyle = mark === undefined ? style : { ...style, [mark]: true }
  if (tag === 'UL' || tag === 'OL') {
    lists.push({ ordered: tag === 'OL', index: 0 })
    el.childNodes.forEach((child) =>
      collectStyledText(child, out, childStyle, lists),
    )
    lists.pop()
    return
  }
  if (tag === 'LI') {
    collectListItem(el, out, childStyle, lists)
    return
  }
  el.childNodes.forEach((child) =>
    collectStyledText(child, out, childStyle, lists),
  )
  if (BLOCK_TAG.test(tag)) out.push('\n')
}

/** Plain text for sending (SMS): paragraph and `<br>` boundaries become
 *  newlines and typed spaces are kept — unlike `htmlToPlainText`, which
 *  flattens the message to one line. Editor formatting survives as characters:
 *  list items get `• ` / `1. ` markers, bold/italic re-base onto the Unicode
 *  math-sans alphabets, underline/strike ride as combining marks. Styled
 *  characters are non-GSM, so a formatted message prices as UCS-2 (70 chars
 *  per segment) — the live segment counter reflects that. */
export function htmlToMultilineText(html: string): string {
  if (typeof document === 'undefined') return html.trim()
  const div = document.createElement('div')
  div.innerHTML = html
  const out: string[] = []
  collectStyledText(div, out, PLAIN_STYLE, [])
  return out.join('').replace(/^\n+/, '').replace(/\s+$/, '')
}

/** Prepare editor HTML for an email body. The editor shows content with
 *  `white-space: pre-wrap`, but mail clients render plain HTML: runs of
 *  spaces collapse and empty `<p></p>` blank lines get zero height. Make
 *  both survive without styling the whole body. */
export function toEmailBodyHtml(html: string): string {
  if (typeof document === 'undefined') return html
  const div = document.createElement('div')
  div.innerHTML = html
  div.querySelectorAll('p').forEach((p) => {
    if (p.childNodes.length === 0) p.appendChild(document.createElement('br'))
  })
  forEachTextNode(div, (text) => {
    const original = text.textContent
    const kept = original.replace(
      / {2,}/g,
      (run) => ` ${'\u00A0'.repeat(run.length - 1)}`,
    )
    if (kept !== original) text.textContent = kept
  })
  return div.innerHTML
}

/** A resolved value can be multi-line (snippet loops); keep the line breaks
 *  visible in the HTML body. */
function toChipNodes(text: string): Node[] {
  return text.split('\n').flatMap((line, index) => {
    const nodes: Node[] = index > 0 ? [document.createElement('br')] : []
    if (line) nodes.push(document.createTextNode(line))
    return nodes
  })
}

/**
 * Replace template chips in editor HTML before sending.
 *
 * Without `values`, each chip becomes its raw `{{token}}` — for mass jobs,
 * where the backend renders per recipient. With `values` (insert token →
 * resolved value from the shipment's template context), chips become the
 * actual value — for direct sends, whose endpoints do not render templates.
 * The template render endpoint resolves `data-insert` itself, so a chip
 * whose insert isn't a known token is kept verbatim; only an unresolved
 * token (e.g. a link that doesn't apply to the shipment's stage) is
 * dropped rather than leaked to the customer.
 */
function looksLikeTemplateToken(insert: string): boolean {
  return /\{.*\}|\{%/.test(insert)
}

/** Path-shaped tokens typed as plain text rather than inserted as chips,
 *  e.g. `{{online_booking}}` or `{customer.first_name}`. */
const TEXT_TOKEN_PATTERN = /\{\{\s*[\w.[\]]+\s*\}\}|\{[\w.[\]]+\}/g

/** The values map is keyed by the backend's `insert` tokens; accept the
 *  typed token in either brace style for the same path. */
function resolveTextToken(
  token: string,
  values: Record<string, string>,
): string {
  const path = token.replace(/^\{+\s*/, '').replace(/\s*\}+$/, '')
  for (const key of [token, `{${path}}`, `{{${path}}}`]) {
    if (key in values) return values[key]
  }
  return ''
}

function forEachTextNode(root: HTMLElement, fn: (text: Text) => void): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const textNodes: Text[] = []
  let node = walker.nextNode()
  while (node) {
    textNodes.push(node as Text)
    node = walker.nextNode()
  }
  textNodes.forEach(fn)
}

function expandTextTokens(
  root: HTMLElement,
  values: Record<string, string>,
): void {
  forEachTextNode(root, (text) => {
    if (text.parentElement?.closest('[data-template-variable]')) return
    const original = text.textContent
    const replaced = original.replace(TEXT_TOKEN_PATTERN, (token) =>
      resolveTextToken(token, values),
    )
    if (replaced !== original) text.textContent = replaced
  })
}

/**
 * Rewrite each chip's visible text to the friendly field label when its
 * insert token is a known variable. Bodies saved before chips carried
 * friendly labels display the raw token, which is unreadable for composite
 * tokens (e.g. the vehicles Jinja loop). Only the display changes — the
 * `data-insert` payload that `expandTemplateChips` sends is preserved, and
 * backfilled from the legacy display text when missing.
 */
export function relabelTemplateChips(
  html: string,
  labelByInsert?: Map<string, string>,
): string {
  if (
    typeof document === 'undefined' ||
    !labelByInsert ||
    labelByInsert.size === 0
  ) {
    return html
  }
  const div = document.createElement('div')
  div.innerHTML = html
  div.querySelectorAll('[data-template-variable]').forEach((el) => {
    const insert = [
      el.getAttribute('data-insert'),
      el.getAttribute('data-value'),
      el.textContent,
    ].find((candidate): candidate is string => Boolean(candidate))
    if (insert === undefined) return
    const label = labelByInsert.get(insert)
    if (label === undefined) return
    el.setAttribute('data-insert', insert)
    el.setAttribute('data-value', label)
    el.textContent = label
  })
  return div.innerHTML
}

export function expandTemplateChips(
  html: string,
  values?: Record<string, string>,
): string {
  if (typeof document === 'undefined') return html
  const div = document.createElement('div')
  div.innerHTML = html
  // Direct sends also resolve (or drop) tokens typed as plain text —
  // the send endpoints do not render templates, so an unresolved key
  // must never reach the recipient.
  if (values) expandTextTokens(div, values)
  div.querySelectorAll('[data-template-variable]').forEach((el) => {
    const insertAttr = el.getAttribute('data-insert')
    if (values) {
      const resolved = insertAttr
        ? (values[insertAttr] ??
          (looksLikeTemplateToken(insertAttr) ? '' : insertAttr))
        : ''
      el.replaceWith(...toChipNodes(resolved))
      return
    }
    if (insertAttr) {
      el.replaceWith(document.createTextNode(insertAttr))
      return
    }
    if (el.textContent) {
      el.replaceWith(document.createTextNode(el.textContent))
      return
    }
    el.remove()
  })
  return div.innerHTML
}
